import { useState, useRef } from 'react';
import { Play, Plus } from 'lucide-react';

interface CanvasStudioProps {
  onImageGenerated: (file: File) => void;
}

interface ImageSlot {
  id: number;
  image: string | null;
}

interface LayoutModel {
  id: string;
  name: string;
  gridClasses: string;
  slotClasses: string[];
}

const LAYOUT_MODELS: Record<number, LayoutModel[]> = {
  1: [
    {
      id: '1-full',
      name: 'Full',
      gridClasses: 'grid-cols-1 grid-rows-1',
      slotClasses: ['']
    }
  ],
  2: [
    {
      id: '2-split-v',
      name: 'Split Vertical',
      gridClasses: 'grid-cols-2 grid-rows-1',
      slotClasses: ['', '']
    },
    {
      id: '2-split-h',
      name: 'Split Horizontal',
      gridClasses: 'grid-cols-1 grid-rows-2',
      slotClasses: ['', '']
    }
  ],
  3: [
    {
      id: '3-hero-left',
      name: 'Hero Left',
      gridClasses: 'grid-cols-2 grid-rows-2',
      slotClasses: ['row-span-2', '', '']
    },
    {
      id: '3-hero-right',
      name: 'Hero Right',
      gridClasses: 'grid-cols-2 grid-rows-2',
      slotClasses: ['', '', 'row-span-2']
    },
    {
      id: '3-hero-top',
      name: 'Hero Top',
      gridClasses: 'grid-cols-2 grid-rows-2',
      slotClasses: ['col-span-2', '', '']
    },
    {
      id: '3-columns',
      name: '3 Columns',
      gridClasses: 'grid-cols-3 grid-rows-1',
      slotClasses: ['', '', '']
    }
  ],
  4: [
    {
      id: '4-grid',
      name: 'Grid 2×2',
      gridClasses: 'grid-cols-2 grid-rows-2',
      slotClasses: ['', '', '', '']
    },
    {
      id: '4-hero-top',
      name: 'Hero Top',
      gridClasses: 'grid-cols-3 grid-rows-2',
      slotClasses: ['col-span-3', '', '', '']
    },
    {
      id: '4-hero-bottom',
      name: 'Hero Bottom',
      gridClasses: 'grid-cols-3 grid-rows-2',
      slotClasses: ['', '', '', 'col-span-3']
    },
    {
      id: '4-columns',
      name: '4 Columns',
      gridClasses: 'grid-cols-4 grid-rows-1',
      slotClasses: ['', '', '', '']
    }
  ],
  5: [
    {
      id: '5-2top-3bottom',
      name: '2 Top / 3 Bottom',
      gridClasses: 'grid-cols-6 grid-rows-2',
      slotClasses: ['col-span-3', 'col-span-3', 'col-span-2', 'col-span-2', 'col-span-2']
    },
    {
      id: '5-hero-center',
      name: 'Hero Center',
      gridClasses: 'grid-cols-4 grid-rows-4',
      slotClasses: ['col-span-2 row-span-2', 'col-span-2 row-span-2', 'col-span-2 row-span-2', 'col-span-1 row-span-2', 'col-span-1 row-span-2']
    },
    {
      id: '5-masonry',
      name: 'Masonry',
      gridClasses: 'grid-cols-6 grid-rows-3',
      slotClasses: ['col-span-3 row-span-2', 'col-span-3', 'col-span-3', 'col-span-2', 'col-span-2', 'col-span-2']
    }
  ],
  6: [
    {
      id: '6-grid-3x2',
      name: 'Grid 3×2',
      gridClasses: 'grid-cols-3 grid-rows-2',
      slotClasses: ['', '', '', '', '', '']
    },
    {
      id: '6-grid-2x3',
      name: 'Grid 2×3',
      gridClasses: 'grid-cols-2 grid-rows-3',
      slotClasses: ['', '', '', '', '', '']
    },
    {
      id: '6-masonry',
      name: 'Masonry',
      gridClasses: 'grid-cols-4 grid-rows-3',
      slotClasses: ['col-span-2 row-span-2', 'col-span-2', 'col-span-2', 'col-span-1', 'col-span-1', 'col-span-2']
    }
  ],
  7: [
    {
      id: '7-hero-left',
      name: 'Hero Left + 6 Grid',
      gridClasses: 'grid-cols-4 grid-rows-3',
      slotClasses: ['col-span-2 row-span-3', 'col-span-1', 'col-span-1', 'col-span-1', 'col-span-1', 'col-span-1', 'col-span-1']
    },
    {
      id: '7-masonry',
      name: 'Masonry',
      gridClasses: 'grid-cols-6 grid-rows-3',
      slotClasses: ['col-span-3 row-span-2', 'col-span-3', 'col-span-3', 'col-span-2', 'col-span-2', 'col-span-2', 'col-span-3']
    }
  ],
  8: [
    {
      id: '8-grid-4x2',
      name: 'Grid 4×2',
      gridClasses: 'grid-cols-4 grid-rows-2',
      slotClasses: ['', '', '', '', '', '', '', '']
    },
    {
      id: '8-grid-2x4',
      name: 'Grid 2×4',
      gridClasses: 'grid-cols-2 grid-rows-4',
      slotClasses: ['', '', '', '', '', '', '', '']
    },
    {
      id: '8-masonry',
      name: 'Masonry',
      gridClasses: 'grid-cols-4 grid-rows-4',
      slotClasses: ['col-span-2 row-span-2', 'col-span-2', 'col-span-2', 'col-span-1 row-span-2', 'col-span-1 row-span-2', 'col-span-1', 'col-span-1', 'col-span-2']
    }
  ],
  9: [
    {
      id: '9-grid',
      name: 'Grid 3×3',
      gridClasses: 'grid-cols-3 grid-rows-3',
      slotClasses: ['', '', '', '', '', '', '', '', '']
    },
    {
      id: '9-masonry',
      name: 'Masonry',
      gridClasses: 'grid-cols-6 grid-rows-4',
      slotClasses: ['col-span-3 row-span-2', 'col-span-3 row-span-2', 'col-span-2', 'col-span-2', 'col-span-2', 'col-span-2', 'col-span-2', 'col-span-2', 'col-span-3']
    }
  ]
};

export function CanvasStudio({ onImageGenerated }: CanvasStudioProps) {
  const [numImages, setNumImages] = useState<number>(3);
  const [selectedModel, setSelectedModel] = useState<string>('3-hero-left');
  const [slots, setSlots] = useState<ImageSlot[]>([
    { id: 0, image: null },
    { id: 1, image: null },
    { id: 2, image: null }
  ]);
  const [showPlayButton, setShowPlayButton] = useState(false);
  const [showBadge, setShowBadge] = useState(false);
  const [draggedSlotId, setDraggedSlotId] = useState<number | null>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [currentSlotId, setCurrentSlotId] = useState<number | null>(null);

  const initializeSlots = (count: number) => {
    const newSlots: ImageSlot[] = [];
    for (let i = 0; i < count; i++) {
      newSlots.push({ id: i, image: null });
    }
    setSlots(newSlots);
  };

  const handleNumImagesChange = (count: number) => {
    setNumImages(count);
    const models = LAYOUT_MODELS[count];
    setSelectedModel(models[0].id);
    initializeSlots(count);
  };

  const compressImage = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Failed to get canvas context'));
            return;
          }

          const maxWidth = 1200;
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              if (blob) {
                const compressedReader = new FileReader();
                compressedReader.onload = (event) => {
                  resolve(event.target?.result as string);
                };
                compressedReader.readAsDataURL(blob);
              } else {
                reject(new Error('Failed to compress image'));
              }
            },
            'image/jpeg',
            0.8
          );
        };
        img.src = e.target?.result as string;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSlotClick = (slotId: number) => {
    setCurrentSlotId(slotId);
    fileInputRef.current?.click();
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0 || currentSlotId === null) return;

    const file = files[0];
    const compressedImage = await compressImage(file);

    setSlots((prev) =>
      prev.map((slot) =>
        slot.id === currentSlotId ? { ...slot, image: compressedImage } : slot
      )
    );

    setCurrentSlotId(null);
    e.target.value = '';
  };

  const handleSlotDrop = async (e: React.DragEvent, slotId: number) => {
    e.preventDefault();

    if (draggedSlotId !== null) {
      setSlots((prev) => {
        const newSlots = [...prev];
        const draggedIndex = newSlots.findIndex((s) => s.id === draggedSlotId);
        const targetIndex = newSlots.findIndex((s) => s.id === slotId);

        const temp = newSlots[draggedIndex].image;
        newSlots[draggedIndex].image = newSlots[targetIndex].image;
        newSlots[targetIndex].image = temp;

        return newSlots;
      });
      setDraggedSlotId(null);
      return;
    }

    const files = Array.from(e.dataTransfer.files).filter((file) =>
      file.type.startsWith('image/')
    );

    if (files.length > 0) {
      const file = files[0];
      const compressedImage = await compressImage(file);

      setSlots((prev) =>
        prev.map((slot) =>
          slot.id === slotId ? { ...slot, image: compressedImage } : slot
        )
      );
    }
  };

  const handleSlotDragStart = (slotId: number) => {
    setDraggedSlotId(slotId);
  };

  const handleSlotDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const getCurrentModel = (): LayoutModel => {
    const models = LAYOUT_MODELS[numImages];
    return models.find(m => m.id === selectedModel) || models[0];
  };

  const mergeImages = async (): Promise<File | null> => {
    const filledSlots = slots.filter((slot) => slot.image !== null);
    if (filledSlots.length === 0 || !canvasRef.current) return null;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;

    const width = 1200;
    const height = 1200;
    canvas.width = width;
    canvas.height = height;

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, width, height);

    const loadImage = (src: string): Promise<HTMLImageElement> => {
      return new Promise((resolve, reject) => {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        img.onload = () => resolve(img);
        img.onerror = reject;
        img.src = src;
      });
    };

    const images = await Promise.all(
      filledSlots.map((slot) => loadImage(slot.image!))
    );

    const model = getCurrentModel();
    const gap = 2;

    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = width;
    tempCanvas.height = height;
    const tempCtx = tempCanvas.getContext('2d');
    if (!tempCtx) return null;

    const container = document.createElement('div');
    container.style.position = 'absolute';
    container.style.width = `${width}px`;
    container.style.height = `${height}px`;
    container.style.visibility = 'hidden';
    container.className = `grid ${model.gridClasses} gap-0.5 bg-black`;
    document.body.appendChild(container);

    const cellElements: HTMLDivElement[] = [];
    slots.forEach((_, index) => {
      const cell = document.createElement('div');
      cell.className = model.slotClasses[index] || '';
      cell.style.position = 'relative';
      container.appendChild(cell);
      cellElements.push(cell);
    });

    await new Promise(resolve => setTimeout(resolve, 100));

    const rects = cellElements.map(el => el.getBoundingClientRect());
    const containerRect = container.getBoundingClientRect();

    document.body.removeChild(container);

    images.forEach((img, index) => {
      const rect = rects[index];
      const x = (rect.left - containerRect.left) * (width / containerRect.width);
      const y = (rect.top - containerRect.top) * (height / containerRect.height);
      const w = rect.width * (width / containerRect.width);
      const h = rect.height * (height / containerRect.height);

      const scale = Math.max(w / img.width, h / img.height);
      const offsetX = x + (w - img.width * scale) / 2;
      const offsetY = y + (h - img.height * scale) / 2;
      ctx.drawImage(img, offsetX, offsetY, img.width * scale, img.height * scale);
    });

    if (showPlayButton) {
      ctx.fillStyle = 'rgba(0, 0, 0, 0.4)';
      ctx.beginPath();
      ctx.arc(width / 2, height / 2, 60, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'white';
      ctx.beginPath();
      ctx.moveTo(width / 2 - 20, height / 2 - 30);
      ctx.lineTo(width / 2 - 20, height / 2 + 30);
      ctx.lineTo(width / 2 + 25, height / 2);
      ctx.closePath();
      ctx.fill();
    }

    if (showBadge) {
      const badgeX = width - 80;
      const badgeY = height - 80;
      const badgeRadius = 30;

      ctx.fillStyle = 'rgba(0, 0, 0, 0.7)';
      ctx.beginPath();
      ctx.arc(badgeX, badgeY, badgeRadius, 0, Math.PI * 2);
      ctx.fill();

      ctx.fillStyle = 'white';
      ctx.font = 'bold 24px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('9+', badgeX, badgeY);
    }

    return new Promise((resolve) => {
      canvas.toBlob((blob) => {
        if (blob) {
          const file = new File([blob], `merged-${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          resolve(file);
        } else {
          resolve(null);
        }
      }, 'image/jpeg', 0.95);
    });
  };

  const handleGenerate = async () => {
    const file = await mergeImages();
    if (file) {
      onImageGenerated(file);
    }
  };

  const filledCount = slots.filter((s) => s.image !== null).length;
  const currentModel = getCurrentModel();
  const availableModels = LAYOUT_MODELS[numImages];

  return (
    <div className="bg-slate-900 rounded-xl border border-slate-800 p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Canvas Studio PRO</h2>
        <div className="bg-slate-800 px-4 py-2 rounded-lg">
          <span className="text-slate-300 text-sm font-medium">
            {filledCount}/{numImages} Images
          </span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-6">
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Jumlah Gambar
          </label>
          <select
            value={numImages}
            onChange={(e) => handleNumImagesChange(Number(e.target.value))}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          >
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <option key={num} value={num}>
                {num} {num === 1 ? 'Image' : 'Images'}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-slate-300 mb-2">
            Model Layout
          </label>
          <select
            value={selectedModel}
            onChange={(e) => setSelectedModel(e.target.value)}
            className="w-full bg-slate-800 border border-slate-700 rounded-lg px-4 py-3 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition"
          >
            {availableModels.map((model) => (
              <option key={model.id} value={model.id}>
                {model.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        onChange={handleFileSelect}
        className="hidden"
      />

      <div className={`w-full max-w-md aspect-square mx-auto grid gap-0.5 bg-black mb-6 ${currentModel.gridClasses}`}>
        {slots.map((slot, index) => (
          <div
            key={slot.id}
            onClick={() => handleSlotClick(slot.id)}
            onDrop={(e) => handleSlotDrop(e, slot.id)}
            onDragOver={handleSlotDragOver}
            draggable={slot.image !== null}
            onDragStart={() => slot.image && handleSlotDragStart(slot.id)}
            className={`relative overflow-hidden ${currentModel.slotClasses[index] || ''} ${
              slot.image ? 'cursor-move' : 'bg-slate-800 cursor-pointer hover:bg-slate-700'
            } transition`}
          >
            {slot.image ? (
              <img
                src={slot.image}
                alt={`Slot ${slot.id + 1}`}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <div className="w-12 h-12 rounded-full bg-slate-900 flex items-center justify-center mb-2">
                  <Plus className="w-6 h-6 text-slate-600" />
                </div>
                <span className="text-slate-600 text-xs font-medium">{slot.id + 1}</span>
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="flex gap-4 mb-6">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showPlayButton}
            onChange={(e) => setShowPlayButton(e.target.checked)}
            className="w-4 h-4 rounded bg-slate-800 border-slate-700"
          />
          <Play className="w-4 h-4 text-slate-400" />
          <span className="text-slate-300 text-sm">Play Button Overlay</span>
        </label>
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={showBadge}
            onChange={(e) => setShowBadge(e.target.checked)}
            className="w-4 h-4 rounded bg-slate-800 border-slate-700"
          />
          <span className="text-slate-300 text-sm">9+ Badge</span>
        </label>
      </div>

      <button
        onClick={handleGenerate}
        disabled={filledCount === 0}
        className="w-full bg-gradient-to-r from-blue-600 to-emerald-600 hover:from-blue-700 hover:to-emerald-700 text-white font-semibold py-3 rounded-lg transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {filledCount > 0
          ? `Generate & Apply to Link (${filledCount}/${numImages})`
          : 'Add images to slots first'}
      </button>

      <canvas ref={canvasRef} className="hidden" />
    </div>
  );
}
